/**
 * Dynamic-field helpers for the Playground.
 *
 * A prompt mixes plain text with `{{name}}` tokens. `{{example}}` is the
 * example text itself and is never a "dynamic field"; every other token is a
 * field that should resolve from the dataset row's ``extra_params``. The
 * backend does the real substitution (`get_request_text`), so the rendering
 * here must follow the same rules: `{{example}}` is replaced (or the example
 * appended when missing), known fields are replaced, unknown ones stay as
 * plain text.
 */

import {
    EXAMPLE_TOKEN,
    extraFieldNamesInTemplate,
    tokenizePromptTemplate,
} from '@/features/model-setup/utils/promptTemplate';

export const EXAMPLE_PLACEHOLDER = `{{${EXAMPLE_TOKEN}}}`;

/**
 * The inference routes take a single prompt string; the design's SYSTEM +
 * USER prompts are joined for dispatch (system first), and the `{{example}}`
 * placeholder is guaranteed so dispatch never sends a half prompt.
 */
export const combinePromptForDispatch = ({ system, user } = {}) => {
    const text = [system, user]
        .map((p) => (p || '').toString().trim())
        .filter(Boolean)
        .join('\n\n');
    if (!text) return '';
    if (text.includes(EXAMPLE_PLACEHOLDER)) return text;
    return `${text}\n${EXAMPLE_PLACEHOLDER}`;
};

/** Dynamic field names (never `example`) referenced by a model's prompts, in first-seen order. */
export const referencedFieldsForPrompt = ({ system, user } = {}) =>
    [...new Set([
        ...extraFieldNamesInTemplate(system || ''),
        ...extraFieldNamesInTemplate(user || ''),
    ])];

/** Partition referenced fields into those the dataset has and those it does not. */
export const splitKnownUnknown = (fields = [], datasetKeys = []) => {
    const available = new Set(datasetKeys || []);
    const known = [];
    const unknown = [];
    for (const field of fields) {
        (available.has(field) ? known : unknown).push(field);
    }
    return { known, unknown };
};

/**
 * Known fields referenced by any slot's prompts — the set editable per example
 * ("Edit n fields"). Order: first appearance across slots.
 */
export const unionKnownFields = (slots = [], promptForSlot, datasetKeys = []) => {
    const all = [];
    for (const slot of slots) {
        all.push(...referencedFieldsForPrompt(promptForSlot(slot)));
    }
    return splitKnownUnknown([...new Set(all)], datasetKeys).known;
};

/** Unknown fields per slot, skipping those the user chose to ignore (`"slotId:field"`). */
export const unknownFieldWarnings = (slots = [], promptForSlot, datasetKeys = [], ignored = new Set()) => {
    const warnings = [];
    for (const slot of slots) {
        const { unknown } = splitKnownUnknown(referencedFieldsForPrompt(promptForSlot(slot)), datasetKeys);
        for (const field of unknown) {
            if (!ignored.has(warningKey(slot.slotId, field))) {
                warnings.push({ slotId: slot.slotId, field });
            }
        }
    }
    return warnings;
};

export const warningKey = (slotId, field) => `${slotId}:${field}`;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Strip every `{{name}}` from a system/user prompt pair (the "Remove" fix). */
export const removeTokenFromPrompts = ({ system, user } = {}, name) => {
    const re = new RegExp(`\\{\\{\\s*${escapeRegExp(name)}\\s*\\}\\}`, 'g');
    const strip = (text) => (text || '').replace(re, '');
    return { system: strip(system), user: strip(user) };
};

const toDisplayValue = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

/** Keep only the values of `fields` (what the backend should substitute). */
export const pickFieldValues = (fieldValues = {}, fields = []) => {
    const picked = {};
    for (const field of fields) {
        if (Object.prototype.hasOwnProperty.call(fieldValues || {}, field)) {
            picked[field] = toDisplayValue(fieldValues[field]);
        }
    }
    return picked;
};

/**
 * Segments of the final prompt as the model receives it.
 *
 * `{ type: 'text', value }` for literal text (including the example and any
 * unknown `{{name}}`, which the backend leaves verbatim), and
 * `{ type: 'field', name, value }` for a substituted known field. The
 * Template view shows `name` as a chip; the As-sent view shows `value` bold.
 */
export const buildFinalPromptSegments = (prompt, { exampleValue = '', fieldValues = {}, knownFields = [] } = {}) => {
    const known = new Set(knownFields);
    // The backend substitutes exact `{{name}}` only, so `{{ name }}` stays text.
    const isExact = (segment) => segment.raw === `{{${segment.name}}}`;
    const segments = tokenizePromptTemplate(prompt).map((segment) => {
        if (segment.type === 'text') return { type: 'text', value: segment.value };
        if (segment.name === EXAMPLE_TOKEN && isExact(segment)) {
            return { type: 'text', value: exampleValue || '' };
        }
        if (known.has(segment.name) && isExact(segment)) {
            return { type: 'field', name: segment.name, value: toDisplayValue(fieldValues?.[segment.name]) };
        }
        return { type: 'text', value: segment.raw };
    });
    return mergeAdjacentText(segments);
};

const mergeAdjacentText = (segments) =>
    segments.reduce((merged, segment) => {
        const last = merged[merged.length - 1];
        if (segment.type === 'text' && last?.type === 'text') {
            last.value += segment.value;
        } else if (segment.type !== 'text' || segment.value !== '') {
            merged.push({ ...segment });
        }
        return merged;
    }, []);

/** Example label for the Edit fields modal title ("Example #01"). */
export const formatExampleNumber = (index) => String(index + 1).padStart(2, '0');

/** "Edit 1 field" / "Edit 10 fields". */
export const editFieldsLabel = (count) => `Edit ${count} ${count === 1 ? 'field' : 'fields'}`;

/** "Edit 2 Dynamic fields - Example #01". */
export const editFieldsTitle = (count, index) =>
    `Edit ${count} Dynamic ${count === 1 ? 'field' : 'fields'} - Example #${formatExampleNumber(index)}`;
